import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudSync as CloudSyncIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Folder as FolderIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import api from "../utils/api";

const SoftwarePermissions = () => {
  const [software, setSoftware] = useState([]);
  const [azureConfigs, setAzureConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [entraidUsers, setEntraidUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingEntraid, setSyncingEntraid] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formError, setFormError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    softwareType: "",
    status: "",
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20,
    total: 0,
  });

  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    version: "",
    vendor: "",
    softwareType: "Third-Party Software",
    classification: "Internal",
    lifecycleStage: "Production",
    installationDate: null,
    licenseType: "",
    licenseExpiryDate: null,
    ownerId: "",
    ownerName: "",
    ownerEmail: "",
    secondaryOwnerId: "",
    secondaryOwnerName: "",
    secondaryOwnerEmail: "",
    riskLevel: "",
    riskAssessmentNotes: "",
    lastReviewDate: null,
    nextReviewDate: null,
    reviewFrequencyMonths: 12,
    notes: "",
    status: "active",
  });

  // Detail view states
  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [syncingPermissions, setSyncingPermissions] = useState(false);

  // Azure DevOps sync states
  const [azureDevOpsDialogOpen, setAzureDevOpsDialogOpen] = useState(false);
  const [azureDevOpsOrg, setAzureDevOpsOrg] = useState("");
  const [azureDevOpsPAT, setAzureDevOpsPAT] = useState("");
  const [syncingAzureDevOps, setSyncingAzureDevOps] = useState(false);
  const [azureDevOpsProjects, setAzureDevOpsProjects] = useState([]);

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Grouped view data
  const [groupedData, setGroupedData] = useState([]);
  const [loadingGrouped, setLoadingGrouped] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDetailData, setSelectedDetailData] = useState(null);
  const [groupedSearch, setGroupedSearch] = useState("");
  const [groupedFilterType, setGroupedFilterType] = useState("");
  const [groupedFilterStatus, setGroupedFilterStatus] = useState("");

  // Bulk assignment states
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignData, setBulkAssignData] = useState({
    ownerId: "",
    ownerEmail: "",
    ownerName: "",
    secondaryOwnerId: "",
    secondaryOwnerEmail: "",
    secondaryOwnerName: "",
  });
  const [loadingUsers] = useState(false);

  // Compute filtered grouped data
  const filteredGroupedData = useMemo(() => {
    let filtered = groupedData;

    // Filter to only show:
    // 1. Managed Identity (Internal Application, is_entraid_app=true, no service principal, no groups/permissions)
    // 2. Enterprise Apps (Enterprise Application type - excludes Microsoft Applications)
    filtered = filtered.filter((item) => {
      const app = item.application;

      // Show Managed Identity: Internal Application with is_entraid_app=true and no service principal
      if (
        app.software_type === "Internal Application" &&
        app.is_entraid_app &&
        !app.entra_id_service_principal_id
      ) {
        // Managed Identity - show it (no groups or permissions)
        return true;
      }

      // Show Enterprise Apps (excludes Microsoft Applications which have software_type = 'Microsoft Application')
      if (app.software_type === "Enterprise Application") {
        return true;
      }

      // Hide everything else (including Microsoft Applications)
      return false;
    });

    if (groupedSearch) {
      const searchLower = groupedSearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.application.name?.toLowerCase().includes(searchLower) ||
          item.application.owner_name?.toLowerCase().includes(searchLower) ||
          item.application.vendor?.toLowerCase().includes(searchLower),
      );
    }

    if (groupedFilterType) {
      filtered = filtered.filter(
        (item) => item.application.software_type === groupedFilterType,
      );
    }

    if (groupedFilterStatus) {
      filtered = filtered.filter(
        (item) => item.application.status === groupedFilterStatus,
      );
    }

    return filtered;
  }, [groupedData, groupedSearch, groupedFilterType, groupedFilterStatus]);

  const fetchAzureConfigs = useCallback(async () => {
    try {
      const response = await api.get("/api/azure-tenant/list");
      setAzureConfigs(response.data.configs || []);
      if (response.data.configs && response.data.configs.length > 0) {
        setSelectedConfigId(response.data.configs[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch Azure configs:", error);
    }
  }, []);

  const fetchGroupedData = useCallback(async () => {
    try {
      setLoadingGrouped(true);
      const response = await api.get("/api/software-permissions/grouped");
      setGroupedData(response.data.grouped || []);
    } catch (error) {
      console.error("Failed to fetch grouped data:", error);
      setError("Failed to load grouped software data");
    } finally {
      setLoadingGrouped(false);
    }
  }, []);

  const fetchSoftware = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        search: filters.search,
        softwareType: filters.softwareType,
        status: filters.status,
      };

      const response = await api.get("/api/software-permissions", { params });
      setSoftware(response.data.software || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0,
      }));
    } catch (error) {
      console.error("Failed to fetch software:", error);
      setError("Failed to load software records");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  useEffect(() => {
    fetchAzureConfigs();
    if (currentTab === 0) {
      fetchGroupedData();
    } else {
      fetchSoftware();
    }
  }, [currentTab, fetchSoftware, fetchAzureConfigs, fetchGroupedData]);

  useEffect(() => {
    if (currentTab === 1) {
      fetchSoftware();
    }
  }, [
    filters,
    pagination.page,
    pagination.pageSize,
    currentTab,
    fetchSoftware,
  ]);

  const handleOpenDetailModal = (detailData) => {
    setSelectedDetailData(detailData);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedDetailData(null);
  };

  // Build rows for MUI DataGrid - simple app rows only
  const buildGridRows = useMemo(() => {
    const rows = [];
    filteredGroupedData.forEach((item) => {
      const app = item.application;

      // Add main app row
      const appRow = {
        id: app.id,
        name: app.name,
        software_type: app.software_type,
        status: app.status,
        owner_name: app.owner_name || "Unassigned",
        projects_count: item.projects?.length || 0,
        roles_count: item.roles?.length || 0,
        permissions_count:
          (item.permissions?.length || 0) +
          (item.azureDevOpsPermissions?.length || 0),
        _detailData: item,
      };

      rows.push(appRow);
    });
    return rows;
  }, [filteredGroupedData]);

  const fetchUsers = async () => {
    if (!selectedConfigId) return;
    try {
      const response = await api.get("/api/software-permissions/users/list", {
        params: { configId: selectedConfigId },
      });
      setEntraidUsers(response.data.users || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleOpenForm = (record = null) => {
    setFormError("");
    if (record) {
      setSelectedSoftware(record);
      setFormData({
        name: record.name || "",
        version: record.version || "",
        vendor: record.vendor || "",
        softwareType: record.software_type || "Third-Party Software",
        classification: record.classification || "Internal",
        lifecycleStage: record.lifecycle_stage || "Production",
        installationDate: record.installation_date
          ? new Date(record.installation_date)
          : null,
        licenseType: record.license_type || "",
        licenseExpiryDate: record.license_expiry_date
          ? new Date(record.license_expiry_date)
          : null,
        ownerId: record.owner_id || "",
        ownerName: record.owner_name || "",
        ownerEmail: record.owner_email || "",
        secondaryOwnerId: record.owner_id_secondary || "",
        secondaryOwnerName: record.owner_name_secondary || "",
        secondaryOwnerEmail: record.owner_email_secondary || "",
        riskLevel: record.risk_level || "",
        riskAssessmentNotes: record.risk_assessment_notes || "",
        lastReviewDate: record.last_review_date
          ? new Date(record.last_review_date)
          : null,
        nextReviewDate: record.next_review_date
          ? new Date(record.next_review_date)
          : null,
        reviewFrequencyMonths: record.review_frequency_months || 12,
        notes: record.notes || "",
        status: record.status || "active",
      });
    } else {
      setSelectedSoftware(null);
      setFormData({
        name: "",
        version: "",
        vendor: "",
        softwareType: "Third-Party Software",
        classification: "Internal",
        lifecycleStage: "Production",
        installationDate: null,
        licenseType: "",
        licenseExpiryDate: null,
        ownerId: "",
        ownerName: "",
        ownerEmail: "",
        secondaryOwnerId: "",
        secondaryOwnerName: "",
        secondaryOwnerEmail: "",
        riskLevel: "",
        riskAssessmentNotes: "",
        lastReviewDate: null,
        nextReviewDate: null,
        reviewFrequencyMonths: 12,
        notes: "",
        status: "active",
      });
    }
    setOpenForm(true);
    if (!record) {
      fetchUsers();
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedSoftware(null);
    setFormError("");
  };

  const handleSave = async () => {
    try {
      setFormError("");
      const data = {
        ...formData,
        installationDate: formData.installationDate
          ? format(formData.installationDate, "yyyy-MM-dd")
          : null,
        licenseExpiryDate: formData.licenseExpiryDate
          ? format(formData.licenseExpiryDate, "yyyy-MM-dd")
          : null,
        lastReviewDate: formData.lastReviewDate
          ? format(formData.lastReviewDate, "yyyy-MM-dd")
          : null,
        nextReviewDate: formData.nextReviewDate
          ? format(formData.nextReviewDate, "yyyy-MM-dd")
          : null,
      };

      if (selectedSoftware) {
        await api.put(`/api/software-permissions/${selectedSoftware.id}`, data);
        setSuccess("Software record updated successfully");
      } else {
        await api.post("/api/software-permissions", data);
        setSuccess("Software record created successfully");
      }

      handleCloseForm();
      fetchSoftware();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Failed to save software:", error);
      const errorMessage =
        error.response?.data?.errors?.[0]?.msg ||
        error.response?.data?.error ||
        "Failed to save software record";
      setFormError(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this software record?")
    ) {
      return;
    }

    try {
      await api.delete(`/api/software-permissions/${id}`);
      setSuccess("Software record deleted successfully");
      fetchSoftware();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Failed to delete software:", error);
      setError(
        error.response?.data?.error || "Failed to delete software record",
      );
    }
  };

  const handleBulkAssign = async () => {
    if (selectedRows.length === 0) {
      setError("Please select at least one software record");
      return;
    }

    if (!bulkAssignData.ownerId && !bulkAssignData.secondaryOwnerId) {
      setFormError("Please select at least one owner (Primary or Secondary)");
      return;
    }

    try {
      setLoading(true);
      setFormError("");

      await api.post("/api/software-permissions/bulk-assign", {
        softwareIds: selectedRows,
        ownerId: bulkAssignData.ownerId || null,
        ownerEmail: bulkAssignData.ownerEmail || null,
        ownerName: bulkAssignData.ownerName || null,
        secondaryOwnerId: bulkAssignData.secondaryOwnerId || null,
        secondaryOwnerEmail: bulkAssignData.secondaryOwnerEmail || null,
        secondaryOwnerName: bulkAssignData.secondaryOwnerName || null,
      });

      setBulkAssignDialogOpen(false);
      setSelectedRows([]);
      setBulkAssignData({
        ownerId: "",
        ownerEmail: "",
        ownerName: "",
        secondaryOwnerId: "",
        secondaryOwnerEmail: "",
        secondaryOwnerName: "",
      });
      setSuccess(
        `Successfully assigned owners to ${selectedRows.length} software record(s)`,
      );
      fetchSoftware();
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Failed to bulk assign owners:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to assign owners. Please try again.";
      setFormError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncEntraid = async () => {
    if (!selectedConfigId) {
      setError("Please select an Azure Tenant Configuration");
      return;
    }

    // Prevent multiple simultaneous syncs
    if (syncingEntraid) {
      return;
    }

    try {
      setSyncingEntraid(true);
      setLoading(true);
      setError("");
      setSuccess("");

      const response = await api.post(
        "/api/software-permissions/sync-entraid",
        {
          configId: selectedConfigId,
        },
      );
      const permMsg = response.data.permissionsSynced
        ? `, ${response.data.permissionsSynced} permissions synced`
        : "";
      const permErrorMsg =
        response.data.permissionErrors?.length > 0
          ? ` (${response.data.permissionErrors.length} permission sync errors)`
          : "";
      const adoMsg = response.data.azureDevOpsSynced
        ? `, ${response.data.azureDevOpsSynced.projects} Azure DevOps projects and ${response.data.azureDevOpsSynced.permissions} permissions synced`
        : "";
      setSuccess(
        `Synced ${response.data.total} applications: ${response.data.created} created, ${response.data.updated} updated${permMsg}${adoMsg}${permErrorMsg}`,
      );

      // Refresh data based on current tab
      if (currentTab === 0) {
        fetchGroupedData();
      } else {
        fetchSoftware();
      }
      setTimeout(() => setSuccess(""), 5000);
    } catch (error) {
      console.error("Failed to sync EntraID applications:", error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to sync EntraID applications",
      );
      setTimeout(() => setError(""), 10000);
    } finally {
      setSyncingEntraid(false);
      setLoading(false);
    }
  };

  const handleViewDetails = async (record) => {
    try {
      setError(""); // Clear any previous errors
      const response = await api.get(`/api/software-permissions/${record.id}`);
      console.log("Detail data received:", response.data);
      console.log("Roles count:", response.data.roles?.length || 0);
      setDetailData(response.data);
      setDetailViewOpen(true);

      // If this is Azure DevOps, fetch projects
      const recordNameLower = record.name?.toLowerCase() || "";
      if (
        recordNameLower.includes("azure devops") ||
        recordNameLower.includes("devops") ||
        recordNameLower === "azure devops"
      ) {
        fetchAzureDevOpsProjects(record.id);
      }
    } catch (error) {
      console.error("Failed to fetch software details:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load software details",
      );
      // Still open the dialog so user can see the error
      setDetailViewOpen(true);
    }
  };

  const fetchAzureDevOpsProjects = async (softwareRecordId) => {
    try {
      const response = await api.get(
        `/api/software-permissions/${softwareRecordId}/azure-devops-projects`,
      );
      setAzureDevOpsProjects(response.data.projects || []);
    } catch (error) {
      console.error("Failed to fetch Azure DevOps projects:", error);
      // Don't show error if endpoint doesn't exist or no projects
    }
  };

  const handleSyncAzureDevOps = async (skipDialog = false) => {
    if (!detailData?.software?.id) {
      setError("Software record not found");
      return;
    }

    // Check if settings exist - if so, sync directly without dialog
    if (skipDialog) {
      try {
        setSyncingAzureDevOps(true);
        const response = await api.post(
          `/api/software-permissions/${detailData.software.id}/sync-azure-devops`,
          {},
        );
        setSuccess(
          `Synced ${response.data.projectsSynced} projects and ${response.data.permissionsSynced} permissions`,
        );
        // Refresh projects
        fetchAzureDevOpsProjects(detailData.software.id);
        // Refresh detail data
        try {
          const refreshResponse = await api.get(
            `/api/software-permissions/${detailData.software.id}`,
          );
          setDetailData(refreshResponse.data);
        } catch (refreshError) {
          console.error("Failed to refresh detail data:", refreshError);
          // Don't show error for refresh failure, just log it
        }
        setTimeout(() => setSuccess(""), 5000);
      } catch (error) {
        console.error("Failed to sync Azure DevOps:", error);
        setError(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to sync Azure DevOps projects. Please configure Azure DevOps settings in Settings.",
        );
        setTimeout(() => setError(""), 10000);
      } finally {
        setSyncingAzureDevOps(false);
      }
      return;
    }

    // If org and PAT are provided, use them; otherwise, use settings
    const payload = {};
    if (azureDevOpsOrg && azureDevOpsPAT) {
      payload.organizationName = azureDevOpsOrg;
      payload.pat = azureDevOpsPAT;
    }

    try {
      setSyncingAzureDevOps(true);
      const response = await api.post(
        `/api/software-permissions/${detailData.software.id}/sync-azure-devops`,
        Object.keys(payload).length > 0 ? payload : {},
      );
      setSuccess(
        `Synced ${response.data.projectsSynced} projects and ${response.data.permissionsSynced} permissions`,
      );
      setAzureDevOpsDialogOpen(false);
      setAzureDevOpsOrg("");
      setAzureDevOpsPAT("");
      // Refresh projects
      fetchAzureDevOpsProjects(detailData.software.id);
      // Refresh detail data
      try {
        const refreshResponse = await api.get(
          `/api/software-permissions/${detailData.software.id}`,
        );
        setDetailData(refreshResponse.data);
      } catch (refreshError) {
        console.error("Failed to refresh detail data:", refreshError);
        // Don't show error for refresh failure, just log it
      }
      setTimeout(() => setSuccess(""), 5000);
    } catch (error) {
      console.error("Failed to sync Azure DevOps:", error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to sync Azure DevOps projects. Please configure Azure DevOps settings in Settings or provide organization name and PAT.",
      );
      setTimeout(() => setError(""), 10000);
    } finally {
      setSyncingAzureDevOps(false);
    }
  };

  const handleSyncAzureDevOpsClick = async () => {
    // Check if settings exist
    try {
      const settingsResponse = await api.get(
        "/api/azure-devops-settings/active",
      );
      if (settingsResponse.data && settingsResponse.data.organizationName) {
        // Settings exist, sync directly
        await handleSyncAzureDevOps(true);
      } else {
        // No settings, show dialog
        setAzureDevOpsDialogOpen(true);
      }
    } catch (error) {
      // Settings not found or error, show dialog
      console.warn(
        "Could not fetch Azure DevOps settings, showing dialog:",
        error,
      );
      setAzureDevOpsDialogOpen(true);
    }
  };

  const handleSyncPermissions = async () => {
    if (!selectedConfigId || !detailData?.software) {
      setError("Please select an Azure Tenant Configuration");
      return;
    }

    try {
      setSyncingPermissions(true);
      const response = await api.post(
        `/api/software-permissions/${detailData.software.id}/sync-permissions`,
        { configId: selectedConfigId },
      );
      setSuccess(
        `Synced ${response.data.rolesSynced} roles and ${response.data.permissionsSynced} permissions`,
      );
      // Refresh detail data
      const refreshResponse = await api.get(
        `/api/software-permissions/${detailData.software.id}`,
      );
      setDetailData(refreshResponse.data);
      setTimeout(() => setSuccess(""), 5000);
    } catch (error) {
      console.error("Failed to sync permissions:", error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to sync permissions",
      );
    } finally {
      setSyncingPermissions(false);
    }
  };

  const columns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(params.row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, params.row.id]);
            } else {
              setSelectedRows(
                selectedRows.filter((id) => id !== params.row.id),
              );
            }
          }}
        />
      ),
    },
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "version",
      headerName: "Version",
      width: 120,
    },
    {
      field: "vendor",
      headerName: "Vendor",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "software_type",
      headerName: "Type",
      width: 180,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: "classification",
      headerName: "Classification",
      width: 130,
      renderCell: (params) => {
        const colors = {
          Public: "success",
          Internal: "info",
          Confidential: "error",
        };
        return (
          <Chip
            label={params.value}
            size="small"
            color={colors[params.value] || "default"}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const colors = {
          active: "success",
          deprecated: "warning",
          retired: "default",
          under_review: "info",
        };
        return (
          <Chip
            label={params.value}
            size="small"
            color={colors[params.value] || "default"}
          />
        );
      },
    },
    {
      field: "owner_name",
      headerName: "Owner",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row)}
            color="primary"
          >
            <SecurityIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenForm(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
      <Box p={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" gap={2}>
            {azureConfigs.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Azure Tenant</InputLabel>
                <Select
                  value={selectedConfigId || ""}
                  label="Azure Tenant"
                  onChange={(e) => setSelectedConfigId(e.target.value)}
                >
                  {azureConfigs.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.tenant_name || config.tenant_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="outlined"
              startIcon={<CloudSyncIcon />}
              onClick={handleSyncEntraid}
              disabled={
                !selectedConfigId ||
                syncingEntraid ||
                (currentTab === 0 ? loadingGrouped : loading)
              }
            >
              {syncingEntraid ? "Syncing..." : "Sync EntraID Apps"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              Add Software
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess("")}
            sx={{ mb: 2 }}
          >
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Applications Overview" />
              <Tab label="All Software" />
            </Tabs>
          </CardContent>
        </Card>

        {/* Tab 0: Grouped View */}
        {currentTab === 0 && (
          <Box>
            {/* Search and Filters */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Search Applications"
                      value={groupedSearch}
                      onChange={(e) => setGroupedSearch(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Software Type</InputLabel>
                      <Select
                        value={groupedFilterType}
                        label="Software Type"
                        onChange={(e) => setGroupedFilterType(e.target.value)}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        <MenuItem value="Internal Application">
                          Internal Application
                        </MenuItem>
                        <MenuItem value="Enterprise Application">
                          Enterprise Application
                        </MenuItem>
                        <MenuItem value="Microsoft Application">
                          Microsoft Application
                        </MenuItem>
                        <MenuItem value="Third-Party Software">
                          Third-Party Software
                        </MenuItem>
                        <MenuItem value="Open Source">Open Source</MenuItem>
                        <MenuItem value="SaaS">SaaS</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={groupedFilterStatus}
                        label="Status"
                        onChange={(e) => setGroupedFilterStatus(e.target.value)}
                      >
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="deprecated">Deprecated</MenuItem>
                        <MenuItem value="retired">Retired</MenuItem>
                        <MenuItem value="under_review">Under Review</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* DataGrid */}
            <Card>
              <CardContent>
                {loadingGrouped ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : groupedData.length === 0 ? (
                  <Alert severity="info">No software records found.</Alert>
                ) : (
                  <Box sx={{ width: "100%" }}>
                    {/* Main Grid with MUI DataGrid */}
                    <DataGrid
                      rows={buildGridRows}
                      columns={[
                        {
                          field: "expand",
                          headerName: "",
                          width: 60,
                          sortable: false,
                          renderCell: (params) => {
                            return (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleOpenDetailModal(params.row._detailData);
                                }}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "action.hover",
                                  },
                                }}
                              >
                                <ExpandMoreIcon />
                              </IconButton>
                            );
                          },
                        },
                        {
                          field: "name",
                          headerName: "Application Name",
                          width: 250,
                          flex: 1,
                          sortable: true,
                          renderCell: (params) => {
                            return params.value || "N/A";
                          },
                        },
                        {
                          field: "software_type",
                          headerName: "Type",
                          width: 200,
                          sortable: true,
                          renderCell: (params) => {
                            return (
                              <Chip
                                label={params.value}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            );
                          },
                        },
                        {
                          field: "status",
                          headerName: "Status",
                          width: 120,
                          sortable: true,
                          renderCell: (params) => {
                            return (
                              <Chip
                                label={params.value}
                                size="small"
                                color={
                                  params.value === "active"
                                    ? "success"
                                    : "default"
                                }
                              />
                            );
                          },
                        },
                        {
                          field: "owner_name",
                          headerName: "Owner",
                          width: 150,
                          sortable: true,
                          renderCell: (params) => {
                            return params.value || "Unassigned";
                          },
                        },
                        {
                          field: "projects_count",
                          headerName: "Projects",
                          width: 100,
                          sortable: true,
                          renderCell: (params) => {
                            return params.value > 0 ? (
                              <Chip
                                icon={<FolderIcon />}
                                label={params.value}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            );
                          },
                        },
                        {
                          field: "roles_count",
                          headerName: "Roles",
                          width: 100,
                          sortable: true,
                          renderCell: (params) => {
                            return params.value > 0 ? (
                              <Chip
                                icon={<GroupIcon />}
                                label={params.value}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            );
                          },
                        },
                        {
                          field: "permissions_count",
                          headerName: "Permissions",
                          width: 120,
                          sortable: true,
                          renderCell: (params) => {
                            return params.value > 0 ? (
                              <Chip
                                icon={<PersonIcon />}
                                label={params.value}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            );
                          },
                        },
                        {
                          field: "actions",
                          headerName: "Actions",
                          width: 110,
                          sortable: false,
                          filterable: false,
                          renderCell: (params) => {
                            const app = params.row?._detailData?.application;
                            if (!app?.id) return null;
                            return (
                              <IconButton
                                size="small"
                                color="primary"
                                title="Edit / assign owner"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenForm(app);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            );
                          },
                        },
                      ]}
                      autoHeight
                      disableSelectionOnClick
                      getRowId={(row) => row.id}
                      initialState={{
                        sorting: {
                          sortModel: [
                            { field: "permissions_count", sort: "desc" },
                          ],
                        },
                      }}
                      sx={{
                        "& .MuiDataGrid-cell": {
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tab 1: All Software (Existing View) */}
        {currentTab === 1 && (
          <Card>
            <CardContent>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Search"
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Software Type</InputLabel>
                    <Select
                      value={filters.softwareType}
                      label="Software Type"
                      onChange={(e) =>
                        setFilters({ ...filters, softwareType: e.target.value })
                      }
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="Internal Application">
                        Internal Application
                      </MenuItem>
                      <MenuItem value="Enterprise Application">
                        Enterprise Application
                      </MenuItem>
                      <MenuItem value="Microsoft Application">
                        Microsoft Application
                      </MenuItem>
                      <MenuItem value="Third-Party Software">
                        Third-Party Software
                      </MenuItem>
                      <MenuItem value="Open Source">Open Source</MenuItem>
                      <MenuItem value="SaaS">SaaS</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) =>
                        setFilters({ ...filters, status: e.target.value })
                      }
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="deprecated">Deprecated</MenuItem>
                      <MenuItem value="retired">Retired</MenuItem>
                      <MenuItem value="under_review">Under Review</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {selectedRows.length > 0 && (
                <Box
                  mb={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    {selectedRows.length} record(s) selected
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setBulkAssignDialogOpen(true);
                      fetchUsers();
                    }}
                  >
                    Assign Owners ({selectedRows.length})
                  </Button>
                </Box>
              )}

              <DataGrid
                rows={software}
                columns={columns}
                loading={loading}
                paginationModel={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                }}
                onPaginationModelChange={(model) => {
                  setPagination({
                    ...pagination,
                    page: model.page,
                    pageSize: model.pageSize,
                  });
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                rowCount={pagination.total}
                paginationMode="server"
                disableRowSelectionOnClick
                autoHeight
                getRowId={(row) => row.id}
              />
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Form Dialog */}
        <Dialog
          open={openForm}
          onClose={handleCloseForm}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedSoftware ? "Edit Software Record" : "Add Software Record"}
          </DialogTitle>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name *"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Version"
                  value={formData.version}
                  onChange={(e) =>
                    setFormData({ ...formData, version: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vendor"
                  value={formData.vendor}
                  onChange={(e) =>
                    setFormData({ ...formData, vendor: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Software Type *</InputLabel>
                  <Select
                    value={formData.softwareType}
                    label="Software Type *"
                    onChange={(e) =>
                      setFormData({ ...formData, softwareType: e.target.value })
                    }
                    required
                  >
                    <MenuItem value="Internal Application">
                      Internal Application
                    </MenuItem>
                    <MenuItem value="Enterprise Application">
                      Enterprise Application
                    </MenuItem>
                    <MenuItem value="Microsoft Application">
                      Microsoft Application
                    </MenuItem>
                    <MenuItem value="Third-Party Software">
                      Third-Party Software
                    </MenuItem>
                    <MenuItem value="Open Source">Open Source</MenuItem>
                    <MenuItem value="SaaS">SaaS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Classification *</InputLabel>
                  <Select
                    value={formData.classification}
                    label="Classification *"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        classification: e.target.value,
                      })
                    }
                    required
                  >
                    <MenuItem value="Public">Public</MenuItem>
                    <MenuItem value="Internal">Internal</MenuItem>
                    <MenuItem value="Confidential">Confidential</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Lifecycle Stage</InputLabel>
                  <Select
                    value={formData.lifecycleStage}
                    label="Lifecycle Stage"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        lifecycleStage: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="Production">Production</MenuItem>
                    <MenuItem value="Development">Development</MenuItem>
                    <MenuItem value="Staging">Staging</MenuItem>
                    <MenuItem value="Test">Test</MenuItem>
                    <MenuItem value="Retired">Retired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Installation Date"
                  value={formData.installationDate}
                  onChange={(date) =>
                    setFormData({ ...formData, installationDate: date })
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="License Type"
                  value={formData.licenseType}
                  onChange={(e) =>
                    setFormData({ ...formData, licenseType: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="License Expiry Date"
                  value={formData.licenseExpiryDate}
                  onChange={(date) =>
                    setFormData({ ...formData, licenseExpiryDate: date })
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find((u) => u.id === formData.ownerId) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      ownerId: newValue?.id || "",
                      ownerName: newValue?.name || "",
                      ownerEmail: newValue?.email || "",
                    });
                  }}
                  onOpen={fetchUsers}
                  renderInput={(params) => (
                    <TextField {...params} label="Owner" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find(
                      (u) => u.id === formData.secondaryOwnerId,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      secondaryOwnerId: newValue?.id || "",
                      secondaryOwnerName: newValue?.name || "",
                      secondaryOwnerEmail: newValue?.email || "",
                    });
                  }}
                  onOpen={fetchUsers}
                  renderInput={(params) => (
                    <TextField {...params} label="Secondary Owner" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Risk Level</InputLabel>
                  <Select
                    value={formData.riskLevel}
                    label="Risk Level"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        riskLevel: e.target.value || null,
                      })
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Risk Assessment Notes"
                  value={formData.riskAssessmentNotes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      riskAssessmentNotes: e.target.value,
                    })
                  }
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Last Review Date"
                  value={formData.lastReviewDate}
                  onChange={(date) =>
                    setFormData({ ...formData, lastReviewDate: date })
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Next Review Date"
                  value={formData.nextReviewDate}
                  onChange={(date) =>
                    setFormData({ ...formData, nextReviewDate: date })
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Review Frequency (months)"
                  type="number"
                  value={formData.reviewFrequencyMonths}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      reviewFrequencyMonths: parseInt(e.target.value) || 12,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detail View Dialog */}
        <Dialog
          open={detailViewOpen}
          onClose={() => setDetailViewOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">
                {detailData?.software?.name || "Software Details"}
              </Typography>
              <Box display="flex" gap={1}>
                {(() => {
                  const name = detailData?.software?.name?.toLowerCase() || "";
                  const isAzureDevOps =
                    name.includes("azure devops") || name.includes("devops");

                  // For Azure DevOps, show a manual sync button only if settings are configured
                  // Otherwise, it syncs automatically when syncing EntraID apps
                  if (isAzureDevOps) {
                    return (
                      <Button
                        variant="outlined"
                        startIcon={<CloudSyncIcon />}
                        onClick={handleSyncAzureDevOpsClick}
                        disabled={syncingAzureDevOps}
                        color="primary"
                        title="Manually sync Azure DevOps projects (normally syncs automatically with EntraID apps)"
                      >
                        {syncingAzureDevOps
                          ? "Syncing..."
                          : "Sync Azure DevOps"}
                      </Button>
                    );
                  }

                  // For other EntraID apps, show sync permissions button
                  if (
                    detailData?.software?.is_entraid_app &&
                    selectedConfigId
                  ) {
                    return (
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleSyncPermissions}
                        disabled={syncingPermissions}
                        title="Sync EntraID application permissions"
                      >
                        {syncingPermissions ? "Syncing..." : "Sync Permissions"}
                      </Button>
                    );
                  }

                  return null;
                })()}
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            )}
            {detailData && (
              <Box>
                <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)}>
                  <Tab label="Details" />
                  <Tab label="Roles" />
                  <Tab label="Permissions" />
                  {(() => {
                    const name =
                      detailData?.software?.name?.toLowerCase() || "";
                    return (
                      name.includes("azure devops") || name.includes("devops")
                    );
                  })() && <Tab label="Azure DevOps Projects" />}
                </Tabs>

                {detailTab === 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Name</Typography>
                        <Typography>{detailData.software.name}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Version</Typography>
                        <Typography>
                          {detailData.software.version || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Vendor</Typography>
                        <Typography>
                          {detailData.software.vendor || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Type</Typography>
                        <Typography>
                          {detailData.software.software_type}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">
                          Classification
                        </Typography>
                        <Chip
                          label={detailData.software.classification}
                          size="small"
                          color={
                            detailData.software.classification ===
                            "Confidential"
                              ? "error"
                              : detailData.software.classification ===
                                  "Internal"
                                ? "info"
                                : "success"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Status</Typography>
                        <Chip
                          label={detailData.software.status}
                          size="small"
                          color={
                            detailData.software.status === "active"
                              ? "success"
                              : detailData.software.status === "deprecated"
                                ? "warning"
                                : "default"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Owner</Typography>
                        <Typography>
                          {detailData.software.owner_name || "Unassigned"}
                        </Typography>
                      </Grid>
                      {detailData.software.entra_id_app_id && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2">
                            EntraID App ID
                          </Typography>
                          <Typography>
                            {detailData.software.entra_id_app_id}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {detailTab === 1 && (
                  <Box sx={{ mt: 2 }}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Role/Permission Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailData.roles && detailData.roles.length > 0 ? (
                            detailData.roles.map((role) => (
                              <TableRow key={role.id}>
                                <TableCell>
                                  {role.role_name}
                                  {role.is_azure_devops_role && (
                                    <Chip
                                      label="Azure DevOps"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {role.role_description || "N/A"}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      role.is_enabled !== false
                                        ? "Enabled"
                                        : "Disabled"
                                    }
                                    size="small"
                                    color={
                                      role.is_enabled !== false
                                        ? "success"
                                        : "default"
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                {(() => {
                                  const name =
                                    detailData?.software?.name?.toLowerCase() ||
                                    "";
                                  const isAzureDevOps =
                                    name.includes("azure devops") ||
                                    name.includes("devops");
                                  return isAzureDevOps
                                    ? "No permission types found. Click 'Sync Azure DevOps' to fetch projects and permissions."
                                    : "No roles defined. Click 'Sync Permissions' to fetch from EntraID.";
                                })()}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {detailTab === 2 && (
                  <Box sx={{ mt: 2 }}>
                    {(() => {
                      const name =
                        detailData?.software?.name?.toLowerCase() || "";
                      const isAzureDevOps =
                        name.includes("azure devops") ||
                        name.includes("devops");
                      const entraidPermissions = detailData.permissions || [];
                      const adoPermissions =
                        detailData.azureDevOpsPermissions || [];
                      const allPermissions = [
                        ...entraidPermissions,
                        ...adoPermissions,
                      ];

                      if (allPermissions.length === 0) {
                        return (
                          <Alert severity="info">
                            {isAzureDevOps
                              ? "No permissions found. Click 'Sync Azure DevOps' to fetch projects and permissions."
                              : "No permissions assigned. Click 'Sync Permissions' to fetch from EntraID."}
                          </Alert>
                        );
                      }

                      return (
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>User/Group</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Project</TableCell>
                                <TableCell>Permission Type</TableCell>
                                <TableCell>Assignment Type</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {allPermissions.map((perm) => (
                                <TableRow
                                  key={`${perm.id}-${perm.is_group_assignment ? "group" : "user"}`}
                                >
                                  <TableCell>
                                    {perm.user_name || perm.group_name || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {perm.user_email || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {perm.project_name || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={
                                        perm.permission_type ||
                                        perm.permission_display_name ||
                                        "N/A"
                                      }
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {perm.is_group_assignment ? (
                                      <Chip
                                        label="Group"
                                        size="small"
                                        color="secondary"
                                      />
                                    ) : perm.assignment_type ? (
                                      <Chip
                                        label={perm.assignment_type}
                                        size="small"
                                      />
                                    ) : (
                                      "Direct"
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={
                                        perm.is_active !== false
                                          ? "Active"
                                          : "Inactive"
                                      }
                                      size="small"
                                      color={
                                        perm.is_active !== false
                                          ? "success"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      );
                    })()}
                  </Box>
                )}
                {detailTab === 3 && (
                  <Box sx={{ mt: 2 }}>
                    {azureDevOpsProjects.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Project Name</TableCell>
                              <TableCell>Organization</TableCell>
                              <TableCell>Visibility</TableCell>
                              <TableCell>State</TableCell>
                              <TableCell>Permissions</TableCell>
                              <TableCell>Last Synced</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {azureDevOpsProjects.map((project) => (
                              <TableRow key={project.id}>
                                <TableCell>{project.project_name}</TableCell>
                                <TableCell>
                                  {project.organization_name}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={project.visibility}
                                    size="small"
                                    color={
                                      project.visibility === "public"
                                        ? "success"
                                        : "default"
                                    }
                                  />
                                </TableCell>
                                <TableCell>{project.state || "N/A"}</TableCell>
                                <TableCell>
                                  {project.permission_count || 0}
                                </TableCell>
                                <TableCell>
                                  {project.last_synced_at
                                    ? format(
                                        new Date(project.last_synced_at),
                                        "dd/MM/yyyy HH:mm",
                                      )
                                    : "Never"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">
                        No Azure DevOps projects found. Click "Sync Azure
                        DevOps" to fetch projects and permissions.
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailViewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Azure DevOps Sync Dialog */}
        <Dialog
          open={azureDevOpsDialogOpen}
          onClose={() => {
            setAzureDevOpsDialogOpen(false);
            setAzureDevOpsOrg("");
            setAzureDevOpsPAT("");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Sync Azure DevOps Projects</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                If Azure DevOps settings are configured in Settings, they will
                be used automatically. Otherwise, enter the organization name
                and PAT below.
              </Alert>
              <TextField
                fullWidth
                label="Organization Name (Optional)"
                value={azureDevOpsOrg}
                onChange={(e) => setAzureDevOpsOrg(e.target.value)}
                placeholder="e.g., myorganization"
                sx={{ mb: 2 }}
                helperText="Leave blank to use settings, or enter to override"
              />
              <TextField
                fullWidth
                label="Personal Access Token (Optional)"
                type="password"
                value={azureDevOpsPAT}
                onChange={(e) => setAzureDevOpsPAT(e.target.value)}
                placeholder="Enter your Azure DevOps PAT"
                helperText="Leave blank to use settings, or enter to override"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAzureDevOpsDialogOpen(false);
                setAzureDevOpsOrg("");
                setAzureDevOpsPAT("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSyncAzureDevOps}
              variant="contained"
              disabled={syncingAzureDevOps}
            >
              {syncingAzureDevOps ? "Syncing..." : "Sync"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detail Modal for Applications Overview */}
        <Dialog
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">
                {selectedDetailData?.application?.name || "Application Details"}
              </Typography>
              <IconButton onClick={handleCloseDetailModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedDetailData &&
              (() => {
                const item = selectedDetailData;
                const hasProjects = item.projects && item.projects.length > 0;
                const hasRoles = item.roles && item.roles.length > 0;
                const hasPermissions =
                  (item.permissions && item.permissions.length > 0) ||
                  (item.azureDevOpsPermissions &&
                    item.azureDevOpsPermissions.length > 0);

                return (
                  <Box sx={{ pt: 2 }}>
                    {/* Projects Section */}
                    {hasProjects && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FolderIcon />
                          Projects ({item.projects.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Project Name</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Visibility</TableCell>
                                <TableCell>State</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.projects.map((project) => (
                                <TableRow key={project.id}>
                                  <TableCell>{project.project_name}</TableCell>
                                  <TableCell>
                                    {project.organization_name}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={project.visibility || "private"}
                                      size="small"
                                      color={
                                        project.visibility === "public"
                                          ? "success"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {project.state || "N/A"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* Roles Section with Permissions nested */}
                    {hasRoles && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <GroupIcon />
                          Roles ({item.roles.length})
                        </Typography>

                        {item.roles.map((role) => {
                          // Get permissions for this role
                          let rolePermissions = [];
                          let adoRolePermissions = [];

                          if (role.is_azure_devops_role) {
                            adoRolePermissions =
                              item.azureDevOpsPermissions?.filter(
                                (perm) =>
                                  perm.permission_type === role.role_name ||
                                  perm.permission_display_name ===
                                    role.role_name,
                              ) || [];
                          } else {
                            rolePermissions =
                              item.permissions?.filter(
                                (perm) =>
                                  perm.assigned_role_name === role.role_name ||
                                  perm.role_name === role.role_name ||
                                  (role.role_id &&
                                    perm.role_id === role.role_id),
                              ) || [];
                          }

                          const hasRolePermissions =
                            rolePermissions.length > 0 ||
                            adoRolePermissions.length > 0;

                          return (
                            <Box
                              key={role.id || role.role_id}
                              sx={{ mb: 2, ml: 2 }}
                            >
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  borderLeft: "3px solid",
                                  borderLeftColor: "secondary.main",
                                  bgcolor: "action.hover",
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  mb={1}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <GroupIcon
                                      fontSize="small"
                                      color="secondary"
                                    />
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                      color="secondary.main"
                                    >
                                      {role.role_name}
                                    </Typography>
                                    {role.is_azure_devops_role && (
                                      <Chip
                                        label="Azure DevOps"
                                        size="small"
                                        color="primary"
                                      />
                                    )}
                                    <Chip
                                      label={
                                        role.is_enabled !== false
                                          ? "Enabled"
                                          : "Disabled"
                                      }
                                      size="small"
                                      color={
                                        role.is_enabled !== false
                                          ? "success"
                                          : "default"
                                      }
                                    />
                                  </Box>
                                  {hasRolePermissions && (
                                    <Chip
                                      icon={<PersonIcon />}
                                      label={`${rolePermissions.length + adoRolePermissions.length} Permission${rolePermissions.length + adoRolePermissions.length !== 1 ? "s" : ""}`}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                    />
                                  )}
                                </Box>

                                {role.role_description && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 1 }}
                                  >
                                    {role.role_description}
                                  </Typography>
                                )}

                                {/* Permissions for this role - Nested under role */}
                                {hasRolePermissions && (
                                  <Box sx={{ mt: 2, ml: 2 }}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                      color="text.secondary"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <PersonIcon fontSize="small" />
                                      Permissions (
                                      {rolePermissions.length +
                                        adoRolePermissions.length}
                                      )
                                    </Typography>

                                    {/* EntraID Permissions for this role */}
                                    {rolePermissions.length > 0 && (
                                      <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        sx={{ mb: 1 }}
                                      >
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>User/Group</TableCell>
                                              <TableCell>Email</TableCell>
                                              <TableCell>
                                                Permission Type
                                              </TableCell>
                                              <TableCell>
                                                Assignment Type
                                              </TableCell>
                                              <TableCell>
                                                Assigned Via Group
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {rolePermissions.map((perm) => (
                                              <TableRow key={perm.id}>
                                                <TableCell>
                                                  {perm.user_name ||
                                                    perm.group_name ||
                                                    "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                  {perm.user_email || "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                  <Chip
                                                    label={perm.permission_type}
                                                    size="small"
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  {perm.assignment_type ? (
                                                    <Chip
                                                      label={
                                                        perm.assignment_type
                                                      }
                                                      size="small"
                                                      color={
                                                        perm.assignment_type ===
                                                        "Direct"
                                                          ? "primary"
                                                          : perm.assignment_type ===
                                                              "Group"
                                                            ? "secondary"
                                                            : "default"
                                                      }
                                                    />
                                                  ) : (
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                    >
                                                      -
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {perm.assigned_via_group_name ? (
                                                    <Chip
                                                      label={
                                                        perm.assigned_via_group_name
                                                      }
                                                      size="small"
                                                      variant="outlined"
                                                    />
                                                  ) : (
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                    >
                                                      -
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}

                                    {/* Azure DevOps Permissions for this role */}
                                    {adoRolePermissions.length > 0 && (
                                      <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                      >
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Project</TableCell>
                                              <TableCell>User/Group</TableCell>
                                              <TableCell>Email</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {adoRolePermissions.map((perm) => (
                                              <TableRow key={perm.id}>
                                                <TableCell>
                                                  {perm.project_name || "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                  {perm.user_name ||
                                                    perm.group_name ||
                                                    "N/A"}
                                                  {perm.is_group_assignment && (
                                                    <Chip
                                                      label="Group"
                                                      size="small"
                                                      color="secondary"
                                                      sx={{ ml: 1 }}
                                                    />
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {perm.user_email || "N/A"}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>
                                )}

                                {!hasRolePermissions && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 1, fontStyle: "italic" }}
                                  >
                                    No permissions assigned to this role
                                  </Typography>
                                )}
                              </Paper>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {/* Permissions not assigned to any role (orphaned permissions) */}
                    {(() => {
                      // Find permissions that don't belong to any role
                      const roleNames = new Set(
                        item.roles?.map((r) => r.role_name) || [],
                      );
                      const orphanedEntraidPerms =
                        item.permissions?.filter(
                          (perm) => !perm.assigned_role_name && !perm.role_name,
                        ) || [];
                      const orphanedAdoPerms =
                        item.azureDevOpsPermissions?.filter(
                          (perm) =>
                            !roleNames.has(perm.permission_type) &&
                            !roleNames.has(perm.permission_display_name),
                        ) || [];

                      if (
                        orphanedEntraidPerms.length > 0 ||
                        orphanedAdoPerms.length > 0
                      ) {
                        // Check for duplicate Owner permissions
                        const ownerPerms = orphanedEntraidPerms.filter(
                          (p) => p.permission_type === "Owner",
                        );
                        const hasDuplicates =
                          ownerPerms.length > 1 &&
                          new Set(ownerPerms.map((p) => p.user_id)).size <
                            ownerPerms.length;

                        return (
                          <Box sx={{ mb: 3 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <PersonIcon />
                                Unassigned Permissions (
                                {orphanedEntraidPerms.length +
                                  orphanedAdoPerms.length}
                                )
                              </Typography>
                              {hasDuplicates && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={async () => {
                                    try {
                                      const response = await api.post(
                                        `/api/software-permissions/${selectedDetailData?.application?.id}/cleanup-duplicates`,
                                      );
                                      // Refresh grouped data to update the modal
                                      await fetchGroupedData();
                                      // Find and update the specific item in selectedDetailData
                                      const refreshResponse = await api.get(
                                        `/api/software-permissions/${selectedDetailData?.application?.id}`,
                                      );
                                      setSelectedDetailData({
                                        ...selectedDetailData,
                                        permissions:
                                          refreshResponse.data.permissions,
                                      });
                                      alert(
                                        `Cleaned up ${response.data.deleted} duplicate permission(s). The list will refresh.`,
                                      );
                                    } catch (error) {
                                      console.error(
                                        "Failed to cleanup duplicates:",
                                        error,
                                      );
                                      alert(
                                        "Failed to cleanup duplicates. Please try again.",
                                      );
                                    }
                                  }}
                                >
                                  Clean Up Duplicates (
                                  {ownerPerms.length -
                                    new Set(ownerPerms.map((p) => p.user_id))
                                      .size}
                                  )
                                </Button>
                              )}
                            </Box>

                            {orphanedEntraidPerms.length > 0 && (
                              <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{ mb: 2 }}
                              >
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>User/Group</TableCell>
                                      <TableCell>Email</TableCell>
                                      <TableCell>Permission Type</TableCell>
                                      <TableCell>Role Name</TableCell>
                                      <TableCell>Assignment Type</TableCell>
                                      <TableCell>Assigned Via Group</TableCell>
                                      <TableCell>Assigned Date</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {orphanedEntraidPerms.map((perm) => (
                                      <TableRow key={perm.id}>
                                        <TableCell>
                                          {perm.user_name ||
                                            perm.group_name ||
                                            "N/A"}
                                        </TableCell>
                                        <TableCell>
                                          {perm.user_email || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={perm.permission_type}
                                            size="small"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {perm.role_name ? (
                                            <Chip
                                              label={perm.role_name}
                                              size="small"
                                              variant="outlined"
                                              color="secondary"
                                            />
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              -
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {perm.assignment_type ? (
                                            <Chip
                                              label={perm.assignment_type}
                                              size="small"
                                              color={
                                                perm.assignment_type ===
                                                "Direct"
                                                  ? "primary"
                                                  : perm.assignment_type ===
                                                      "Group"
                                                    ? "secondary"
                                                    : "default"
                                              }
                                            />
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              -
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {perm.assigned_via_group_name ? (
                                            <Chip
                                              label={
                                                perm.assigned_via_group_name
                                              }
                                              size="small"
                                              variant="outlined"
                                            />
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              -
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {perm.assigned_date ? (
                                            <Typography variant="body2">
                                              {format(
                                                new Date(perm.assigned_date),
                                                "dd/MM/yyyy HH:mm",
                                              )}
                                            </Typography>
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              -
                                            </Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}

                            {orphanedAdoPerms.length > 0 && (
                              <TableContainer
                                component={Paper}
                                variant="outlined"
                              >
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Project</TableCell>
                                      <TableCell>User/Group</TableCell>
                                      <TableCell>Email</TableCell>
                                      <TableCell>Permission Type</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {orphanedAdoPerms.map((perm) => (
                                      <TableRow key={perm.id}>
                                        <TableCell>
                                          {perm.project_name || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                          {perm.user_name ||
                                            perm.group_name ||
                                            "N/A"}
                                          {perm.is_group_assignment && (
                                            <Chip
                                              label="Group"
                                              size="small"
                                              color="secondary"
                                              sx={{ ml: 1 }}
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {perm.user_email || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={perm.permission_type}
                                            size="small"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        );
                      }
                      return null;
                    })()}

                    {/* No data message */}
                    {!hasProjects && !hasRoles && !hasPermissions && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No projects, roles, or permissions configured for this
                        application.
                      </Alert>
                    )}
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailModal}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Assign Dialog */}
        <Dialog
          open={bulkAssignDialogOpen}
          onClose={() => setBulkAssignDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Assign Owners to {selectedRows.length} Record(s)
          </DialogTitle>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find((u) => u.id === bulkAssignData.ownerId) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setBulkAssignData({
                      ...bulkAssignData,
                      ownerId: newValue?.id || "",
                      ownerEmail: newValue?.email || "",
                      ownerName: newValue?.name || "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Primary Owner"
                      placeholder="Search for primary owner..."
                      fullWidth
                    />
                  )}
                  loading={loadingUsers}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    entraidUsers.find(
                      (u) => u.id === bulkAssignData.secondaryOwnerId,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setBulkAssignData({
                      ...bulkAssignData,
                      secondaryOwnerId: newValue?.id || "",
                      secondaryOwnerEmail: newValue?.email || "",
                      secondaryOwnerName: newValue?.name || "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Secondary Owner"
                      placeholder="Search for secondary owner..."
                      fullWidth
                    />
                  )}
                  loading={loadingUsers}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssign}
              variant="contained"
              disabled={
                loading ||
                (!bulkAssignData.ownerId && !bulkAssignData.secondaryOwnerId)
              }
            >
              {loading ? "Assigning..." : "Assign Owners"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default SoftwarePermissions;
